import type {
  FeeLineItem,
  FeeRecipeRow,
  FeeRule,
  FeeRuleGroup,
  FundingBehavior,
  FundraiserScenario,
  MoneyCents,
  PlatformComparisonResult,
  ResolvedPlatformPricing,
} from '../models/fundraising-comparison.models';

interface PreparedCharge {
  intendedGiftCents: MoneyCents;
  donorExtraCents: MoneyCents;
  donorExtraRecipient: 'organization' | 'platform' | null;
}

export class PricingEngine {
  compare(
    scenario: FundraiserScenario,
    pricing: ResolvedPlatformPricing,
  ): PlatformComparisonResult {
    const warnings: string[] = [];
    const charges = this.prepareCharges(scenario, pricing, warnings);
    const feeLineItems = this.calculateFeeLineItems(pricing, charges);
    const organizationPaidFeeCents = this.sumLineItems(
      feeLineItems.filter(
        (item) =>
          item.paidBy === 'organization' && item.includedInPrimaryResult,
      ),
    );
    const annualCostCents = this.sumLineItems(
      feeLineItems.filter((item) => !item.includedInPrimaryResult),
    );
    const donorPaidExtraCents = charges.reduce(
      (total, charge) => total + charge.donorExtraCents,
      0,
    );
    const donorContributionToOrganizationCents = charges
      .filter((charge) => charge.donorExtraRecipient === 'organization')
      .reduce((total, charge) => total + charge.donorExtraCents, 0);

    this.addDonorLineItems(feeLineItems, charges, pricing.fundingBehavior);

    const organizationRetainedCents =
      scenario.intendedGiftTotalCents +
      donorContributionToOrganizationCents -
      organizationPaidFeeCents;
    const organizationCostCents = Math.max(
      0,
      organizationPaidFeeCents - donorContributionToOrganizationCents,
    );
    const effectiveOrganizationCostBasisPoints =
      scenario.intendedGiftTotalCents > 0
        ? this.roundRatio(
            organizationCostCents * 10_000,
            scenario.intendedGiftTotalCents,
          )
        : 0;
    const organizationRetainedBasisPoints =
      scenario.intendedGiftTotalCents > 0
        ? this.roundRatio(
            organizationRetainedCents * 10_000,
            scenario.intendedGiftTotalCents,
          )
        : 0;

    const donorPaidExtraEstimated =
      pricing.fundingBehavior.kind !== 'optional-tip-to-platform' &&
      !(
        pricing.fundingBehavior.kind === 'optional-fee-coverage' &&
        pricing.fundingBehavior.estimated === false
      );
    const donorPaidExtraNote = this.donorExtraNote(pricing.fundingBehavior);
    const feeRecipeRows = this.createFeeRecipeRows(pricing, charges);

    return {
      platformId: pricing.platform.id,
      platformName: pricing.platform.displayName,
      featured: pricing.platform.featured ?? false,
      regionLabel: pricing.regionLabel,
      evidence: pricing.evidence,
      intendedGiftTotalCents: scenario.intendedGiftTotalCents,
      organizationRetainedCents,
      organizationRetainedBasisPoints,
      organizationCostCents,
      donorPaidExtraCents,
      donorPaidExtraEstimated,
      donorPaidExtraNote,
      annualCostCents,
      effectiveOrganizationCostBasisPoints,
      feeLineItems,
      feeRecipeRows,
      compactFeeFormula: this.createCompactFeeFormula(pricing, charges),
      warnings,
      qualifications: pricing.platform.qualifications,
      disclosures: pricing.platform.disclosures,
      publishedFacts: pricing.platform.publishedFacts,
      sources: pricing.sources,
      optionGroups: pricing.optionGroups,
      optionSelections: pricing.optionSelections,
      configurationUi: pricing.platform.configurationUi,
      tieredPercentageScale: this.createTieredPercentageScale(pricing, charges),
    };
  }

  private prepareCharges(
    scenario: FundraiserScenario,
    pricing: ResolvedPlatformPricing,
    warnings: string[],
  ): PreparedCharge[] {
    const funding = pricing.fundingBehavior;
    if (
      funding.kind === 'organization-absorbs' ||
      funding.kind === 'optional-tip-to-platform'
    ) {
      return this.emptyCharges(scenario);
    }

    if (funding.kind === 'optional-contribution-to-organization') {
      warnings.push(funding.assumptionLabel);
      const participatingCount = this.participatingTransactionCount(
        scenario.transactions.length,
        funding.participationBasisPoints,
      );
      return scenario.transactions.map((transaction, index) => {
        const participates = this.isParticipatingTransaction(
          index,
          scenario.transactions.length,
          participatingCount,
        );
        return {
          intendedGiftCents: transaction.intendedGiftCents,
          donorExtraCents: participates
            ? this.optionalContributionCents(
                transaction.intendedGiftCents,
                funding,
              )
            : 0,
          donorExtraRecipient: participates ? 'organization' : null,
        };
      });
    }

    if (funding.kind === 'donor-service-charge-to-organization') {
      return scenario.transactions.map((transaction) => ({
        intendedGiftCents: transaction.intendedGiftCents,
        donorExtraCents: this.calculatePercentage(
          transaction.intendedGiftCents,
          funding.rateBasisPoints,
        ),
        donorExtraRecipient: 'organization',
      }));
    }

    const referencedGroups = pricing.feeGroups.filter((group) =>
      funding.feeGroupIds.includes(group.id),
    );
    const participationBasisPoints =
      funding.kind === 'optional-fee-coverage'
        ? (funding.participationBasisPoints ?? 0)
        : 10_000;
    if (funding.kind === 'optional-fee-coverage') {
      warnings.push(funding.assumptionLabel);
    }
    const participatingCount = this.participatingTransactionCount(
      scenario.transactions.length,
      participationBasisPoints,
    );

    return scenario.transactions.map((transaction, index) => {
      const participates = this.isParticipatingTransaction(
        index,
        scenario.transactions.length,
        participatingCount,
      );
      const donorExtraCents = participates
        ? this.findGrossUpCents(transaction.intendedGiftCents, referencedGroups)
        : 0;
      return {
        intendedGiftCents: transaction.intendedGiftCents,
        donorExtraCents,
        donorExtraRecipient: donorExtraCents > 0 ? 'organization' : null,
      };
    });
  }

  private emptyCharges(scenario: FundraiserScenario): PreparedCharge[] {
    return scenario.transactions.map((transaction) => ({
      intendedGiftCents: transaction.intendedGiftCents,
      donorExtraCents: 0,
      donorExtraRecipient: null,
    }));
  }

  private optionalContributionCents(
    intendedGiftCents: MoneyCents,
    funding: Extract<
      FundingBehavior,
      { kind: 'optional-contribution-to-organization' }
    >,
  ): MoneyCents {
    if (funding.contributionKind === 'fixed') {
      return funding.amountCents ?? 0;
    }
    return this.calculatePercentage(
      intendedGiftCents,
      funding.rateBasisPoints ?? 0,
    );
  }

  private calculateFeeLineItems(
    pricing: ResolvedPlatformPricing,
    charges: PreparedCharge[],
  ): FeeLineItem[] {
    return pricing.feeGroups.flatMap((group) =>
      this.calculateFeeGroup(group, charges, pricing.optionSelections),
    );
  }

  private calculateFeeGroup(
    group: FeeRuleGroup,
    charges: PreparedCharge[],
    optionSelections: ResolvedPlatformPricing['optionSelections'],
  ): FeeLineItem[] {
    const ruleItems = group.rules.map((rule, ruleIndex) => ({
      id: `${group.id}-${ruleIndex + 1}`,
      feeGroupId: group.id,
      label: this.ruleLabel(group.label, rule, charges, optionSelections),
      category: group.category,
      paidBy: group.paidBy,
      recipient: group.recipient,
      amountCents: this.calculateRuleAmount(
        group,
        rule,
        charges,
        optionSelections,
      ),
      includedInPrimaryResult: group.scope !== 'annual',
    })) satisfies FeeLineItem[];

    if (group.maximumCents === undefined) {
      return ruleItems;
    }
    const totalCents = this.sumLineItems(ruleItems);
    if (totalCents <= group.maximumCents) {
      return ruleItems;
    }
    return [
      ...ruleItems,
      {
        id: `${group.id}-cap`,
        feeGroupId: group.id,
        label: `${group.label} cap adjustment`,
        category: group.category,
        paidBy: group.paidBy,
        recipient: group.recipient,
        amountCents: group.maximumCents - totalCents,
        includedInPrimaryResult: group.scope !== 'annual',
      },
    ];
  }

  private calculateRuleAmount(
    group: FeeRuleGroup,
    rule: FeeRule,
    charges: PreparedCharge[],
    optionSelections: ResolvedPlatformPricing['optionSelections'],
  ): MoneyCents {
    if (rule.kind === 'fixed') {
      return rule.amountCents;
    }
    if (rule.kind === 'per-transaction') {
      return charges.length * rule.amountCents;
    }
    const rateBasisPoints = this.ruleRateBasisPoints(
      rule,
      charges,
      optionSelections,
    );
    if (rateBasisPoints === null) return 0;
    if (group.scope === 'transaction') {
      return charges.reduce(
        (total, charge) =>
          total +
          this.calculatePercentage(
            this.percentageBase(charge, rule.appliedTo),
            rateBasisPoints,
          ),
        0,
      );
    }
    const aggregateBase = charges.reduce(
      (total, charge) => total + this.percentageBase(charge, rule.appliedTo),
      0,
    );
    return this.calculatePercentage(aggregateBase, rateBasisPoints);
  }

  private percentageBase(
    charge: PreparedCharge,
    appliedTo: 'intended-gift' | 'donor-charge',
  ): MoneyCents {
    return appliedTo === 'donor-charge'
      ? charge.intendedGiftCents + charge.donorExtraCents
      : charge.intendedGiftCents;
  }

  private addDonorLineItems(
    items: FeeLineItem[],
    charges: PreparedCharge[],
    funding: FundingBehavior,
  ): void {
    const donorContributionCents = charges
      .filter((charge) => charge.donorExtraRecipient === 'organization')
      .reduce((total, charge) => total + charge.donorExtraCents, 0);
    if (donorContributionCents > 0) {
      let label = 'Additional donor charges received';
      if (funding.kind === 'optional-contribution-to-organization') {
        label = 'Estimated optional donor contributions';
      } else if (funding.kind === 'donor-service-charge-to-organization') {
        label = 'Donor-paid service charges';
      }
      items.unshift({
        id: 'donor-contribution-to-organization',
        feeGroupId: 'donor-contribution',
        label,
        category: 'donor-contribution',
        paidBy: 'donor',
        recipient: 'organization',
        amountCents: donorContributionCents,
        includedInPrimaryResult: true,
      });
    }
  }

  private createFeeRecipeRows(
    pricing: ResolvedPlatformPricing,
    charges: PreparedCharge[],
  ): FeeRecipeRow[] {
    const groups = pricing.feeGroups;
    const funding = pricing.fundingBehavior;
    const rows: FeeRecipeRow[] = groups.map((group) => ({
      id: group.id,
      label: group.label,
      recipe: group.rules
        .map((rule) =>
          this.recipePart(rule, group.scope, charges, pricing.optionSelections),
        )
        .join(' + '),
      payerLabel: this.payerLabel(group, funding),
    }));

    if (groups.filter((group) => group.scope !== 'annual').length === 0) {
      rows.unshift({
        id: 'no-organization-fees',
        label: 'Organization fees',
        recipe: 'No platform or processing fees',
        payerLabel: 'Organization',
      });
    }

    if (funding.kind === 'optional-contribution-to-organization') {
      rows.push({
        id: 'optional-donor-contribution',
        label: 'Optional donor contribution',
        recipe: funding.assumptionLabel,
        payerLabel: 'Participating donors',
      });
    } else if (funding.kind === 'optional-tip-to-platform') {
      rows.push({
        id: 'optional-donor-tip',
        label: 'Optional donor tips',
        recipe: 'Chosen by donors; not estimated',
        payerLabel: 'Participating donors',
      });
    }
    return rows;
  }

  private createCompactFeeFormula(
    pricing: ResolvedPlatformPricing,
    charges: PreparedCharge[],
  ): string {
    const groups = pricing.feeGroups;
    const totals = {
      transactionRateBasisPoints: 0,
      transactionFixedCents: 0,
      fundraiserRateBasisPoints: 0,
      fundraiserFixedCents: 0,
    };

    for (const group of groups.filter(
      (candidate) => candidate.scope !== 'annual',
    )) {
      for (const rule of group.rules) {
        const rateBasisPoints = this.ruleRateBasisPoints(
          rule,
          charges,
          pricing.optionSelections,
        );
        if (rateBasisPoints !== null) {
          if (group.scope === 'transaction') {
            totals.transactionRateBasisPoints += rateBasisPoints;
          } else {
            totals.fundraiserRateBasisPoints += rateBasisPoints;
          }
        } else if (rule.kind === 'per-transaction') {
          totals.transactionFixedCents += rule.amountCents;
        } else if (rule.kind === 'fixed') {
          totals.fundraiserFixedCents += rule.amountCents;
        }
      }
    }

    const parts: string[] = [];
    if (
      totals.transactionRateBasisPoints > 0 ||
      totals.transactionFixedCents > 0
    ) {
      const transactionParts: string[] = [];
      if (totals.transactionRateBasisPoints > 0) {
        transactionParts.push(
          this.formatPercentage(totals.transactionRateBasisPoints),
        );
      }
      if (totals.transactionFixedCents > 0) {
        transactionParts.push(this.formatCents(totals.transactionFixedCents));
      }
      parts.push(`${transactionParts.join(' + ')} per donation`);
    }
    if (totals.fundraiserRateBasisPoints > 0) {
      const rateLabel = groups
        .filter((group) => group.scope === 'fundraiser')
        .every((group) => group.category === 'revenue-share')
        ? 'revenue share'
        : 'fundraising fees';
      parts.push(
        `${this.formatPercentage(totals.fundraiserRateBasisPoints)} ${rateLabel}`,
      );
    }
    if (totals.fundraiserFixedCents > 0) {
      parts.push(
        `${this.formatCents(totals.fundraiserFixedCents)} per fundraiser`,
      );
    }

    return parts.length > 0
      ? parts.join(' + ')
      : 'No platform or processing fees';
  }

  private payerLabel(group: FeeRuleGroup, funding: FundingBehavior): string {
    if (group.paidBy === 'donor') return 'Donors';
    if (
      funding.kind === 'donor-pays-gross-up' &&
      funding.feeGroupIds.includes(group.id)
    ) {
      return 'Donors, as an added charge';
    }
    if (
      funding.kind === 'optional-fee-coverage' &&
      funding.feeGroupIds.includes(group.id) &&
      (funding.participationBasisPoints ?? 0) > 0
    ) {
      return 'Donors who opt in; organization otherwise';
    }
    return 'Organization';
  }

  private donorExtraNote(funding: FundingBehavior): string | null {
    if (funding.kind === 'optional-tip-to-platform') return funding.note;
    if (funding.kind === 'optional-fee-coverage')
      return funding.assumptionLabel;
    if (funding.kind === 'optional-contribution-to-organization') {
      return funding.assumptionLabel;
    }
    return null;
  }

  private findGrossUpCents(
    intendedGiftCents: MoneyCents,
    feeGroups: FeeRuleGroup[],
  ): MoneyCents {
    if (intendedGiftCents <= 0 || feeGroups.length === 0) return 0;
    let low = 0;
    let high = Math.max(100, Math.ceil(intendedGiftCents / 5));
    while (
      intendedGiftCents +
        high -
        this.transactionFeeForSingleCharge(
          intendedGiftCents,
          intendedGiftCents + high,
          feeGroups,
        ) <
      intendedGiftCents
    ) {
      high *= 2;
      if (!Number.isSafeInteger(high)) {
        throw new Error('Unable to gross up the configured transaction fees.');
      }
    }
    while (low < high) {
      const midpoint = Math.floor((low + high) / 2);
      const netCents =
        intendedGiftCents +
        midpoint -
        this.transactionFeeForSingleCharge(
          intendedGiftCents,
          intendedGiftCents + midpoint,
          feeGroups,
        );
      if (netCents >= intendedGiftCents) high = midpoint;
      else low = midpoint + 1;
    }
    return low;
  }

  private transactionFeeForSingleCharge(
    intendedGiftCents: MoneyCents,
    donorChargeCents: MoneyCents,
    feeGroups: FeeRuleGroup[],
  ): MoneyCents {
    return feeGroups
      .flatMap((group) => group.rules)
      .reduce((total, rule) => {
        if (rule.kind === 'percentage') {
          return (
            total +
            this.calculatePercentage(
              rule.appliedTo === 'donor-charge'
                ? donorChargeCents
                : intendedGiftCents,
              rule.rateBasisPoints,
            )
          );
        }
        if (rule.kind === 'per-transaction') return total + rule.amountCents;
        return total;
      }, 0);
  }

  private participatingTransactionCount(
    transactionCount: number,
    participationBasisPoints: number,
  ): number {
    return Math.max(
      0,
      Math.min(
        transactionCount,
        this.roundRatio(transactionCount * participationBasisPoints, 10_000),
      ),
    );
  }

  private isParticipatingTransaction(
    transactionIndex: number,
    transactionCount: number,
    participatingCount: number,
  ): boolean {
    if (transactionCount === 0 || participatingCount === 0) return false;
    return (
      Math.floor(
        ((transactionIndex + 1) * participatingCount) / transactionCount,
      ) > Math.floor((transactionIndex * participatingCount) / transactionCount)
    );
  }

  private calculatePercentage(
    amountCents: MoneyCents,
    rateBasisPoints: number,
  ): MoneyCents {
    return this.roundRatio(amountCents * rateBasisPoints, 10_000);
  }

  private roundRatio(numerator: number, denominator: number): number {
    return Math.floor((numerator + denominator / 2) / denominator);
  }

  private sumLineItems(items: FeeLineItem[]): MoneyCents {
    return items.reduce((total, item) => total + item.amountCents, 0);
  }

  private ruleLabel(
    label: string,
    rule: FeeRule,
    charges: PreparedCharge[],
    optionSelections: ResolvedPlatformPricing['optionSelections'],
  ): string {
    const rateBasisPoints = this.ruleRateBasisPoints(
      rule,
      charges,
      optionSelections,
    );
    if (rateBasisPoints !== null) {
      return `${label}: ${this.formatPercentage(rateBasisPoints)}`;
    }
    if (rule.kind === 'per-transaction') {
      return `${label}: ${this.formatCents(rule.amountCents)} per donation`;
    }
    return label;
  }

  private recipePart(
    rule: FeeRule,
    scope: FeeRuleGroup['scope'],
    charges: PreparedCharge[],
    optionSelections: ResolvedPlatformPricing['optionSelections'],
  ): string {
    const rateBasisPoints = this.ruleRateBasisPoints(
      rule,
      charges,
      optionSelections,
    );
    if (rateBasisPoints !== null) return this.formatPercentage(rateBasisPoints);
    if (rule.kind === 'per-transaction') {
      return `${this.formatCents(rule.amountCents)} per donation`;
    }
    if (rule.kind === 'fixed') {
      return scope === 'annual'
        ? `${this.formatCents(rule.amountCents)} per year`
        : `${this.formatCents(rule.amountCents)} per fundraiser`;
    }
    return '';
  }

  private ruleRateBasisPoints(
    rule: FeeRule,
    charges: PreparedCharge[],
    optionSelections: ResolvedPlatformPricing['optionSelections'],
  ): number | null {
    if (rule.kind === 'percentage') return rule.rateBasisPoints;
    if (rule.kind === 'percentage-from-option') {
      const configuredRate = Number(optionSelections[rule.optionGroupId]);
      if (!Number.isInteger(configuredRate)) return 0;
      return rule.valueMeaning === 'organization-retained-rate'
        ? 10_000 - configuredRate
        : configuredRate;
    }
    if (rule.kind === 'tiered-percentage-by-total') {
      const tier = this.selectedTier(
        rule.tiers,
        this.intendedGiftTotal(charges),
      );
      return tier?.rateBasisPoints ?? 0;
    }
    return null;
  }

  private intendedGiftTotal(charges: PreparedCharge[]): MoneyCents {
    return charges.reduce(
      (total, charge) => total + charge.intendedGiftCents,
      0,
    );
  }

  private selectedTier<
    T extends { minAmountCents: MoneyCents; maxAmountCents?: MoneyCents },
  >(tiers: T[], amountCents: MoneyCents): T | undefined {
    return tiers.find(
      (tier) =>
        amountCents >= tier.minAmountCents &&
        (tier.maxAmountCents === undefined ||
          amountCents <= tier.maxAmountCents),
    );
  }

  private createTieredPercentageScale(
    pricing: ResolvedPlatformPricing,
    charges: PreparedCharge[],
  ): PlatformComparisonResult['tieredPercentageScale'] {
    const presentation = pricing.platform.tieredScalePresentation;
    if (!presentation) return undefined;
    const group = pricing.feeGroups.find(
      (candidate) => candidate.id === presentation.feeGroupId,
    );
    const rule = group?.rules.find(
      (candidate) => candidate.kind === 'tiered-percentage-by-total',
    );
    if (!rule || rule.kind !== 'tiered-percentage-by-total') return undefined;
    const amountCents = this.intendedGiftTotal(charges);
    const selectedTierIndex = rule.tiers.findIndex(
      (tier) => this.selectedTier([tier], amountCents) !== undefined,
    );
    return {
      presentation,
      tiers: rule.tiers,
      selectedTierIndex,
    };
  }

  private formatPercentage(rateBasisPoints: number): string {
    return `${Number((rateBasisPoints / 100).toFixed(2))}%`;
  }

  private formatCents(amountCents: MoneyCents): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amountCents / 100);
  }
}
